'use client';

import { Suspense } from 'react';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { AdminHeader } from '@/components/layout';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

/**
 * Client component for admin layout.
 *
 * Provides minimal layout with:
 * - Simple admin header (no full dashboard navigation)
 * - Protected route wrapper
 * - Loading states
 * - Consistent container styling
 *
 * @param children - React children to render inside the layout
 * @returns Admin layout with header and main content area
 *
 * @example
 * ```tsx
 * <AdminLayoutClient>
 *   <AdminPage />
 * </AdminLayoutClient>
 * ```
 *
 * @see {@link ./admin-header.tsx} - Admin header component
 * @see {@link ../auth/protected-route.tsx} - Authentication wrapper
 */
export function AdminLayoutClient({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <LoadingSpinner
          message="Loading..."
          className="text-muted-foreground"
          spinnerClassName="border-current"
        />
      }
    >
      <ProtectedRoute>
        <AdminHeader />
        <main className="container pt-4 pb-24 sm:py-6 px-4 sm:px-6">{children}</main>
      </ProtectedRoute>
    </Suspense>
  );
}
